import { useEffect, useRef } from 'react';
import { useDesignStore } from '../store/designStore';
import { HISTORY_SNAPSHOT_PROPS } from '../store/historyStore';
import { safeGetItem } from '../lib/safeStorage';

const API_BASE = '/api';

// Extracted from useAutoSave's own setInterval callback (2026-08-27) so a
// save can also be fired ON DEMAND — see designStore.ts's dirtySideKeys
// comment for the real bug this closes: switching to the Preview tab right
// after an edit used to wait up to 30s (this hook's own interval) before a
// fresh embossing/embroidery render even started. Reads all live state via
// useDesignStore.getState() rather than hook context, same as the original
// interval callback did (and for the same reason: a caller like
// DesignerApp.tsx's tab-switch handler is not itself a hook, and even if it
// were, closing over live store values via context would risk the exact
// stale-closure bug the original code's own comment already documents for
// the interval case). Never throws — callers (the interval below, and any
// on-demand caller) treat a save as fire-and-forget, matching this
// function's own original inline behavior.
export async function saveDesignNow(): Promise<void> {
  const store = useDesignStore.getState();
  const { canvasInstances, sides, productConfig, savedDesignId, setSavedDesignId, getCustomizePrice,
    nameNumbers, vdpData, activeColorVariantId, hasSelectedColorVariant, bumpSavedDesignVersion,
    clearDirtySideKeys } = store;

  if (!productConfig || !sides.length) return;

  // Only the active side has a live Fabric canvas in `canvasInstances` —
  // DesignerWorkspace reuses one <canvas> across sides, disposing the
  // previous one on every switch (see row 2.6's fix in CartPopup.tsx/
  // DesignerHeader.tsx for the full story). Without seeding from
  // `sideColorCountJsonCache` here too, this would periodically overwrite
  // the same `design_id` with only the active side's content, silently
  // erasing every other side (and every OTHER print-color count's snapshot
  // on a side that has more than one) from a design a manual save had
  // correctly captured moments earlier. Nested shape:
  // Record<sideKey, Record<colorCountKey, FabricJSON>> — see
  // designStore.ts's sideColorCountJsonCache field comment.
  const canvasMap: Record<string, Record<string, any>> = structuredClone(store.sideColorCountJsonCache);
  for (const side of sides) {
    const canvas = canvasInstances.get(side.sideKey);
    if (canvas && canvas.getObjects().length > 0) {
      const countKey = store.resolveActiveColorCountKey(side);
      canvasMap[side.sideKey] = {
        ...canvasMap[side.sideKey],
        [countKey]: canvas.toJSON(HISTORY_SNAPSHOT_PROPS),
      };
    }
  }

  if (!Object.keys(canvasMap).length) return;

  const canvasJson = btoa(JSON.stringify(canvasMap));
  const token = safeGetItem('session', 'customedge_token');

  try {
    const res = await fetch(`${API_BASE}/designs`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        product_id:          productConfig.productId,
        // "My Design" retention key — see CartPopup.tsx/DesignerHeader.tsx
        // for the full rationale. This is often the very first Design row
        // for a fresh design, and its omission here meant that row (and
        // every subsequent autosave update) never carried the guest's
        // identity — the actual root cause of designs surviving to a real
        // order with customerId/sessionId both null, invisible to My
        // Designs forever.
        sessionId:           safeGetItem('session', 'customedge_design_hash') ?? undefined,
        canvasJson,
        previewJson:         canvasJson,
        imageWidth:          sides[0]?.canvasWidth ?? 400,
        imageHeight:         sides[0]?.canvasHeight ?? 400,
        design_id:           savedDesignId ?? undefined,
        customizePrices:     getCustomizePrice(),
        name_number:         nameNumbers.length ? JSON.stringify(nameNumbers) : undefined,
        vdp_data:            vdpData.length ? JSON.stringify(vdpData) : undefined,
        // Was reading the long-dead `printingMethod` store field (never
        // set by any UI -- see DesignerHeader.tsx's own handleSave
        // comment) instead of the real live selection state, so autosave
        // ALWAYS sent printingMethodData: undefined regardless of what
        // the customer actually picked. Harmless on its own (designs.ts's
        // update branch preserves the existing value when this field is
        // falsy) -- but if autosave's own POST is ever the request that
        // creates the row (before the customer's first explicit Save),
        // that first row is born with printingMethodData permanently
        // null, and depending on exactly when/whether a later explicit
        // Save re-sends the real value, the null can persist through to
        // the order. Found 2026-08-05 via a real live order (PX120181,
        // increment 000042935) whose saved Design had two genuinely
        // different per-position sides (voorzijde=Zeefdruk,
        // achterzijde=DTF Transfer, both correctly tagged) but
        // printingMethodData was still null. Mirrors DesignerHeader.tsx's
        // handleSave shape exactly so both save paths agree.
        // Plain object, NOT pre-stringified — the outer `body: JSON.stringify({...})`
        // below already serializes this whole payload once; wrapping this
        // field in its own JSON.stringify() first (as this code did through
        // 2026-08-06) double-encodes it; the backend's own
        // `JSON.stringify(body.printingMethodData)` (designs.ts) then
        // stringifies THAT string again, so `printingMethodDetails` ends up
        // storing `"{\"positions\":...}"` (a JSON string containing an
        // escaped JSON string) instead of a real JSON object — every reader
        // (`GET /:id/widget`'s `JSON.parse()`, the admin order detail
        // route, useDesignWip.ts) then gets back a STRING, not an object,
        // silently breaking every consumer of positions/methodByPosition/
        // colorByPosition for any design saved by autosave (the highest-
        // frequency save path) since this bug's introduction 2026-08-05.
        // Matches DesignerHeader.tsx's own printingMethodData shape exactly
        // (that save path never had this bug — always sent a plain object).
        //
        // Read via store (not destructured at the top of this function
        // outside the fields already pulled) for the same reason canvasMap
        // above reads sideColorCountJsonCache that way: this function reads
        // ALL live state fresh via getState() on every call, so there is no
        // stale-closure risk here regardless — kept as store.<field> purely
        // to match the original code's own structure this was extracted
        // from, not because it's meaningfully different from destructuring.
        printingMethodData: (store.selectedPrintMethod || store.selectedPrintColorOption != null || store.selectedPrintPositions.length > 0)
          ? {
              method: store.selectedPrintMethod ?? undefined,
              printColorOption: store.selectedPrintColorOption ?? undefined,
              positions: store.selectedPrintPositions.length ? store.selectedPrintPositions : undefined,
              methodByPosition: Object.keys(store.selectedPrintMethodByPosition).length ? store.selectedPrintMethodByPosition : undefined,
              colorByPosition: Object.keys(store.selectedPrintColorByPosition).length ? store.selectedPrintColorByPosition : undefined,
            }
          : undefined,
        // Same rationale as DesignerHeader.tsx's own save payload — without
        // this, a design that's only ever autosaved (customer never clicks
        // Save/Add-to-Cart) never persists which color variant they picked.
        colorVariantId:      hasSelectedColorVariant ? (activeColorVariantId ?? undefined) : undefined,
      }),
    });
    // Check response before parsing — a non-OK response body may not be JSON
    if (!res.ok) return;
    const data = await res.json().catch(() => null);
    if (data?.designId && !savedDesignId) setSavedDesignId(data.designId);
    // REAL BUG found+fixed 2026-08-21 (live user report: editing text
    // and saving again after the Preview tab had already reached DONE
    // once left it stuck showing the old render) — see designStore.ts's
    // own comment on savedDesignVersion for the full mechanism. Bumped
    // on every successful save (autosave included, since a customer
    // editing text and simply waiting is a real, common path to hit
    // this — not just the explicit Save button).
    if (data?.designId) {
      bumpSavedDesignVersion();
      // Every save above sends the FULL current canvasMap for every side
      // with content, not a partial/single-side save — so a successful
      // save means every side is now current, not just whichever one(s)
      // triggered this call. See designStore.ts's dirtySideKeys comment.
      clearDirtySideKeys();
    }
  } catch {
    // Save failures are non-fatal; the user can always save manually
  }
}

export function useAutoSave(intervalMs = 30_000) {
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    timerRef.current = setInterval(() => { saveDesignNow(); }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [intervalMs]);
}
